// src/utils/exportUtils.js - Reusable CSV export utility

/**
 * Escapes a cell value for CSV formatting.
 * Handles commas, double quotes, and newlines.
 */
function escapeCSVValue(val) {
  if (val === null || val === undefined) return '';
  const str = String(val).trim();
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Exports an array of objects to a CSV file and triggers download in browser.
 * 
 * @param {Array<Object>} data - The array of data rows to export
 * @param {string} filename - The target filename (e.g. 'patients_export_2026-09-30.csv')
 * @param {Array<{ key: string, label: string, formatter?: Function }>} columns - Column definitions
 */
export function exportToCSV(data = [], filename = 'export.csv', columns = []) {
  if (!Array.isArray(data) || data.length === 0) {
    alert('No data available to export.');
    return;
  }

  if (!columns || columns.length === 0) {
    console.warn('exportToCSV: No columns specified, using object keys.');
    const sample = data[0];
    columns = Object.keys(sample).map(k => ({ key: k, label: k }));
  }

  // Header row
  const headerRow = columns.map(col => escapeCSVValue(col.label || col.key)).join(',');

  // Data rows
  const rows = data.map(item => {
    return columns.map(col => {
      let val = item[col.key];
      if (typeof col.formatter === 'function') {
        val = col.formatter(val, item);
      }
      return escapeCSVValue(val);
    }).join(',');
  });

  const csvContent = '\uFEFF' + [headerRow, ...rows].join('\r\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename.endsWith('.csv') ? filename : `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
